// Medical Scores Database - Complete Collection Part 1
// Scores A-C from medicalcul.free.fr

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Eye,
  Ear,
  Scissors,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Users,
  Zap,
  Target,
  Shield
} from 'lucide-react';

export const SCORES_PART_1 = {
  // 4PEPS Score - Probabilité embolie pulmonaire
  fourPeps: {
    name: 'Score 4PEPS',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Probabilité embolie pulmonaire',
    color: 'rose',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 50 ans (0 pt)' },
        { value: 2, label: '50-64 ans (2 pts)' },
        { value: 4, label: '≥ 65 ans (4 pts)' }
      ]},
      { id: 'sex', label: 'Sexe masculin', type: 'checkbox', points: 2 },
      { id: 'dyspnea', label: 'Dyspnée brutale', type: 'checkbox', points: 1 },
      { id: 'syncope', label: 'Syncope', type: 'checkbox', points: 1 },
      { id: 'hr', label: 'FC > 100/min', type: 'checkbox', points: 1 },
      { id: 'oxygen', label: 'SpO₂ < 95%', type: 'checkbox', points: 1 },
      { id: 'leg', label: 'Douleur/œdème unilatéral MI', type: 'checkbox', points: 1 },
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 2 },
      { id: 'hormones', label: 'Contraception/THS', type: 'checkbox', points: 2 },
      { id: 'surgery', label: 'Chirurgie < 3 mois', type: 'checkbox', points: 1 },
      { id: 'immobilization', label: 'Immobilisation > 3 jours', type: 'checkbox', points: 1 },
      { id: 'history', label: 'ATCD MTEV', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = values.age || 0;
      if (values.sex) total += 2;
      if (values.dyspnea) total += 1;
      if (values.syncope) total += 1;
      if (values.hr) total += 1;
      if (values.oxygen) total += 1;
      if (values.leg) total += 1;
      if (values.cancer) total += 2;
      if (values.hormones) total += 2;
      if (values.surgery) total += 1;
      if (values.immobilization) total += 1;
      if (values.history) total += 2;
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: 'Probabilité très faible', detail: 'EP peu probable, pas d\'examen complémentaire' };
      if (score <= 5) return { level: 'warning', text: 'Probabilité faible', detail: 'Dosage D-dimères recommandé' };
      if (score <= 8) return { level: 'warning', text: 'Probabilité intermédiaire', detail: 'Angioscanner thoracique' };
      return { level: 'danger', text: 'Probabilité élevée', detail: 'Angioscanner + anticoagulation' };
    },
    formula: '4PEPS = Âge + Sexe + Symptômes + FR'
  },

  // A-DIVA Score - Difficulté perfusion
  adiva: {
    name: 'Score A-DIVA',
    category: 'Soins infirmiers',
    icon: Syringe,
    description: 'Prédiction difficulté à perfuser un adulte',
    color: 'emerald',
    fields: [
      { id: 'visible', label: 'Veine visible après garrot', type: 'select', options: [
        { value: 0, label: 'Oui (0 pt)' },
        { value: 1, label: 'Non (1 pt)' }
      ]},
      { id: 'palpable', label: 'Veine palpable après garrot', type: 'select', options: [
        { value: 0, label: 'Oui (0 pt)' },
        { value: 2, label: 'Non (2 pts)' }
      ]},
      { id: 'diameter', label: 'Diamètre estimé < 3mm', type: 'checkbox', points: 1 },
      { id: 'history', label: 'ATCD accès veineux difficile', type: 'checkbox', points: 1 },
      { id: 'edema', label: 'Œdème du bras', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = (values.visible || 0) + (values.palpable || 0);
      if (values.diameter) total += 1;
      if (values.history) total += 1;
      if (values.edema) total += 1;
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `A-DIVA ${score}`, detail: 'Accès veineux probablement facile' };
      if (score <= 3) return { level: 'warning', text: `A-DIVA ${score}`, detail: 'Difficulté modérée - Opérateur expérimenté recommandé' };
      return { level: 'danger', text: `A-DIVA ${score}`, detail: 'Accès veineux difficile - Échographie, voie centrale à discuter' };
    },
    formula: 'A-DIVA = Visibilité + Palpabilité + Diamètre + ATCD + Œdème'
  },

  // AGGIR - Calcul GIR
  aggir: {
    name: 'Grille AGGIR',
    category: 'Gériatrie',
    icon: User,
    description: 'Évaluation autonomie - Groupes Iso-Ressources',
    color: 'amber',
    fields: [
      { id: 'coherence', label: 'Cohérence', type: 'select', options: [
        { value: 'A', label: 'A - Fait seul totalement' },
        { value: 'B', label: 'B - Fait partiellement' },
        { value: 'C', label: 'C - Ne fait pas' }
      ]},
      { id: 'orientation', label: 'Orientation', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'toilette', label: 'Toilette', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'habillage', label: 'Habillage', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'alimentation', label: 'Alimentation', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'elimination', label: 'Élimination', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'transferts', label: 'Transferts', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'deplacement_int', label: 'Déplacements intérieur', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'deplacement_ext', label: 'Déplacements extérieur', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]},
      { id: 'communication', label: 'Communication à distance', type: 'select', options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }
      ]}
    ],
    calculate: (values) => {
      // Calcul simplifié du GIR basé sur les variables discriminantes
      const discriminant = ['coherence', 'orientation', 'toilette', 'habillage', 'alimentation', 'elimination', 'transferts', 'deplacement_int'];
      let cCount = 0;
      discriminant.forEach(key => {
        if (values[key] === 'C') cCount++;
      });
      let gir;
      if (cCount >= 7) gir = 1;
      else if (cCount >= 5) gir = 2;
      else if (cCount >= 3) gir = 3;
      else if (cCount >= 1) gir = 4;
      else gir = 6;
      return { score: gir, max: 6, unit: 'GIR' };
    },
    interpret: (score) => {
      const descriptions = {
        1: 'Dépendance totale - Fonctions mentales et corporelles très altérées',
        2: 'Dépendance majeure - Fonctions mentales ou locomotrices altérées',
        3: 'Dépendance partielle - Fonctions mentales conservées, locomotion altérée',
        4: 'Dépendance ponctuelle - Aide pour transferts ou activités corporelles',
        5: 'Aide ponctuelle - Ménage, repas',
        6: 'Autonome'
      };
      if (score <= 2) return { level: 'danger', text: `GIR ${score}`, detail: descriptions[score] + ' - APA à taux plein' };
      if (score <= 4) return { level: 'warning', text: `GIR ${score}`, detail: descriptions[score] + ' - APA possible' };
      return { level: 'success', text: `GIR ${score}`, detail: descriptions[score] };
    },
    formula: 'AGGIR: 10 variables discriminantes codées A/B/C → GIR 1-6'
  },

  // ALBI Score - Fonction hépatique
  albi: {
    name: 'Score ALBI',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Évaluation fonction hépatique (CHC)',
    color: 'yellow',
    fields: [
      { id: 'albumin', label: 'Albumine (g/L)', type: 'number', min: 10, max: 60 },
      { id: 'bilirubin', label: 'Bilirubine totale (µmol/L)', type: 'number', min: 1, max: 500 }
    ],
    calculate: (values) => {
      const alb = values.albumin || 35;
      const bil = values.bilirubin || 10;
      const albi = (Math.log10(bil) * 0.66) + (alb * -0.085);
      return { score: albi.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s <= -2.60) return { level: 'success', text: `ALBI Grade 1`, detail: 'Bonne fonction hépatique' };
      if (s <= -1.39) return { level: 'warning', text: `ALBI Grade 2`, detail: 'Fonction hépatique intermédiaire' };
      return { level: 'danger', text: `ALBI Grade 3`, detail: 'Fonction hépatique altérée' };
    },
    formula: 'ALBI = (log10 Bili × 0.66) + (Albumine × -0.085)'
  },

  // Algoplus - Douleur personne âgée
  algoplus: {
    name: 'Échelle Algoplus',
    category: 'Gériatrie',
    icon: User,
    description: 'Douleur de la personne âgée non communicante',
    color: 'amber',
    fields: [
      { id: 'face', label: 'Expressions du visage (froncement sourcils, grimaces)', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 1, label: 'Oui (1 pt)' }
      ]},
      { id: 'regard', label: 'Regard (inattentif, fixe, lointain, pleurs)', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 1, label: 'Oui (1 pt)' }
      ]},
      { id: 'plaintes', label: 'Plaintes (gémissement, cris)', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 1, label: 'Oui (1 pt)' }
      ]},
      { id: 'corps', label: 'Corps (position antalgique, protection zone)', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 1, label: 'Oui (1 pt)' }
      ]},
      { id: 'comportement', label: 'Comportement (agitation, agressivité)', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 1, label: 'Oui (1 pt)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `Algoplus ${score}/5`, detail: 'Pas de douleur évidente' };
      return { level: 'danger', text: `Algoplus ${score}/5`, detail: 'Douleur probable - Traitement antalgique à envisager' };
    },
    formula: 'Algoplus ≥ 2 = douleur probable'
  },

  // Ann Arbor - Classification lymphomes
  annArbor: {
    name: 'Classification Ann Arbor',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Classification des lymphomes',
    color: 'red',
    fields: [
      { id: 'stage', label: 'Stade', type: 'select', options: [
        { value: 1, label: 'Stade I - Une seule région ganglionnaire' },
        { value: 2, label: 'Stade II - Deux régions ganglionnaires du même côté du diaphragme' },
        { value: 3, label: 'Stade III - Atteinte des deux côtés du diaphragme' },
        { value: 4, label: 'Stade IV - Atteinte diffuse d\'un ou plusieurs organes' }
      ]},
      { id: 'symptoms', label: 'Symptômes B', type: 'select', options: [
        { value: 'A', label: 'A - Asymptomatique' },
        { value: 'B', label: 'B - Fièvre >38°C, sueurs nocturnes, perte >10% poids' }
      ]},
      { id: 'extranodal', label: 'Atteinte extra-ganglionnaire', type: 'checkbox', points: 0 },
      { id: 'bulky', label: 'Masse bulky (>10cm)', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      const stage = values.stage || 1;
      const suffix = (values.symptoms || 'A') + (values.extranodal ? 'E' : '') + (values.bulky ? 'X' : '');
      return { score: stage, max: 4, suffix: suffix };
    },
    interpret: (score, calculated, values) => {
      const suffix = values.symptoms || 'A';
      if (score <= 2 && suffix === 'A') return { level: 'success', text: `Stade ${score}${values.symptoms || 'A'}`, detail: 'Stade localisé, bon pronostic' };
      if (score <= 2) return { level: 'warning', text: `Stade ${score}${values.symptoms || 'A'}`, detail: 'Stade localisé avec symptômes B' };
      return { level: 'danger', text: `Stade ${score}${values.symptoms || 'A'}`, detail: 'Stade avancé' };
    },
    formula: 'Ann Arbor I-IV + A/B ± E (extra-nodal) ± X (bulky)'
  },

  // Binet - LLC
  binet: {
    name: 'Classification de Binet',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Classification leucémie lymphoïde chronique',
    color: 'red',
    fields: [
      { id: 'areas', label: 'Nombre d\'aires lymphoïdes atteintes', type: 'select', options: [
        { value: 0, label: '0 aire' },
        { value: 1, label: '1 aire' },
        { value: 2, label: '2 aires' },
        { value: 3, label: '3 aires' },
        { value: 4, label: '4 aires' },
        { value: 5, label: '5 aires' }
      ]},
      { id: 'anemia', label: 'Hémoglobine < 10 g/dL', type: 'checkbox', points: 0 },
      { id: 'thrombocytopenia', label: 'Plaquettes < 100 G/L', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      let stage;
      if (values.anemia || values.thrombocytopenia) stage = 'C';
      else if ((values.areas || 0) >= 3) stage = 'B';
      else stage = 'A';
      return { score: stage, max: 'C' };
    },
    interpret: (score) => {
      if (score === 'A') return { level: 'success', text: 'Binet A', detail: '< 3 aires, pas de cytopénie. Survie > 10 ans' };
      if (score === 'B') return { level: 'warning', text: 'Binet B', detail: '≥ 3 aires, pas de cytopénie. Survie ~5 ans' };
      return { level: 'danger', text: 'Binet C', detail: 'Anémie et/ou thrombopénie. Survie ~2 ans' };
    },
    formula: 'Binet A: <3 aires | B: ≥3 aires | C: cytopénies'
  },

  // CAGE-DETA - Dépendance alcool
  cageDeta: {
    name: 'Questionnaire CAGE-DETA',
    category: 'Toxicologie',
    icon: Pill,
    description: 'Dépistage dépendance alcoolique',
    color: 'orange',
    fields: [
      { id: 'cut', label: 'Avez-vous ressenti le besoin de Diminuer votre consommation?', type: 'checkbox', points: 1 },
      { id: 'annoyed', label: 'Votre Entourage vous a-t-il fait des remarques?', type: 'checkbox', points: 1 },
      { id: 'guilt', label: 'Avez-vous eu l\'impression de Trop boire?', type: 'checkbox', points: 1 },
      { id: 'eye', label: 'Avez-vous besoin d\'Alcool dès le matin pour vous sentir en forme?', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `CAGE ${score}/4`, detail: 'Pas de dépendance' };
      if (score === 1) return { level: 'warning', text: `CAGE ${score}/4`, detail: 'Usage à risque possible' };
      return { level: 'danger', text: `CAGE ${score}/4`, detail: 'Dépendance probable (Se ≥ 2 = 93%)' };
    },
    formula: 'CAGE (DETA): Diminuer, Entourage, Trop, Alcool matin'
  },

  // Caprini 2005
  caprini: {
    name: 'Score de Caprini',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Risque thromboembolique post-opératoire',
    color: 'emerald',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 41 ans (0 pt)' },
        { value: 1, label: '41-60 ans (1 pt)' },
        { value: 2, label: '61-74 ans (2 pts)' },
        { value: 3, label: '≥ 75 ans (3 pts)' }
      ]},
      { id: 'surgery', label: 'Type chirurgie', type: 'select', options: [
        { value: 1, label: 'Mineure (1 pt)' },
        { value: 2, label: 'Majeure > 45min (2 pts)' },
        { value: 5, label: 'Arthroplastie (5 pts)' }
      ]},
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 2 },
      { id: 'obesity', label: 'IMC > 25', type: 'checkbox', points: 1 },
      { id: 'varices', label: 'Varices', type: 'checkbox', points: 1 },
      { id: 'atcdTVP', label: 'ATCD TVP/EP', type: 'checkbox', points: 3 },
      { id: 'familyTVP', label: 'ATCD familial MTEV', type: 'checkbox', points: 3 },
      { id: 'immobility', label: 'Immobilité > 72h', type: 'checkbox', points: 2 },
      { id: 'contraception', label: 'Contraception/THS', type: 'checkbox', points: 1 },
      { id: 'thrombophilia', label: 'Thrombophilie connue', type: 'checkbox', points: 3 }
    ],
    calculate: (values) => {
      let total = (values.age || 0) + (values.surgery || 1);
      if (values.cancer) total += 2;
      if (values.obesity) total += 1;
      if (values.varices) total += 1;
      if (values.atcdTVP) total += 3;
      if (values.familyTVP) total += 3;
      if (values.immobility) total += 2;
      if (values.contraception) total += 1;
      if (values.thrombophilia) total += 3;
      return { score: total, max: 22 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `Caprini ${score}`, detail: 'Risque faible - Mobilisation précoce' };
      if (score <= 4) return { level: 'warning', text: `Caprini ${score}`, detail: 'Risque modéré - Prophylaxie mécanique ± pharmacologique' };
      if (score <= 8) return { level: 'danger', text: `Caprini ${score}`, detail: 'Risque élevé - Prophylaxie pharmacologique' };
      return { level: 'danger', text: `Caprini ${score}`, detail: 'Risque très élevé - Prophylaxie intensive' };
    },
    formula: 'Caprini: stratification risque MTEV post-op'
  },

  // CIWA-Ar - Sevrage alcoolique
  ciwaAr: {
    name: 'Score CIWA-Ar',
    category: 'Toxicologie',
    icon: Pill,
    description: 'Sévérité sevrage alcoolique',
    color: 'orange',
    fields: [
      { id: 'nausea', label: 'Nausées/vomissements', type: 'select', options: [
        { value: 0, label: '0 - Absents' },
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Nausées intermittentes' },
        { value: 5, label: '5' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Nausées constantes, vomissements' }
      ]},
      { id: 'tremor', label: 'Tremblements', type: 'select', options: [
        { value: 0, label: '0 - Absents' },
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Modérés bras tendus' },
        { value: 5, label: '5' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Sévères même bras au repos' }
      ]},
      { id: 'sweating', label: 'Sueurs', type: 'select', options: [
        { value: 0, label: '0 - Absentes' }, { value: 1, label: '1' },
        { value: 2, label: '2' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Sueurs visibles' },
        { value: 5, label: '5' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Sueurs profuses' }
      ]},
      { id: 'anxiety', label: 'Anxiété', type: 'select', options: [
        { value: 0, label: '0 - Calme' }, { value: 1, label: '1' },
        { value: 2, label: '2' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Modérément anxieux' },
        { value: 5, label: '5' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Panique' }
      ]},
      { id: 'agitation', label: 'Agitation', type: 'select', options: [
        { value: 0, label: '0 - Normale' }, { value: 1, label: '1' },
        { value: 2, label: '2' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Modérément agité' },
        { value: 5, label: '5' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Déambule sans cesse' }
      ]},
      { id: 'tactile', label: 'Troubles tactiles', type: 'select', options: [
        { value: 0, label: '0 - Absents' }, { value: 1, label: '1 - Très légers' },
        { value: 2, label: '2 - Légers' }, { value: 3, label: '3 - Modérés' },
        { value: 4, label: '4 - Hallucinations modérées' },
        { value: 5, label: '5 - Sévères' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Hallucinations continues' }
      ]},
      { id: 'auditory', label: 'Troubles auditifs', type: 'select', options: [
        { value: 0, label: '0 - Absents' }, { value: 1, label: '1' },
        { value: 2, label: '2' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Hallucinations modérées' },
        { value: 5, label: '5' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Hallucinations continues' }
      ]},
      { id: 'visual', label: 'Troubles visuels', type: 'select', options: [
        { value: 0, label: '0 - Absents' }, { value: 1, label: '1' },
        { value: 2, label: '2' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Hallucinations modérées' },
        { value: 5, label: '5' }, { value: 6, label: '6' },
        { value: 7, label: '7 - Hallucinations continues' }
      ]},
      { id: 'headache', label: 'Céphalées', type: 'select', options: [
        { value: 0, label: '0 - Absentes' }, { value: 1, label: '1' },
        { value: 2, label: '2 - Légères' }, { value: 3, label: '3' },
        { value: 4, label: '4 - Modérées' }, { value: 5, label: '5' },
        { value: 6, label: '6' }, { value: 7, label: '7 - Très sévères' }
      ]},
      { id: 'orientation', label: 'Orientation', type: 'select', options: [
        { value: 0, label: '0 - Orienté' },
        { value: 1, label: '1 - Incertain date' },
        { value: 2, label: '2 - Désorienté date <2j' },
        { value: 3, label: '3 - Désorienté date >2j' },
        { value: 4, label: '4 - Désorienté lieu et/ou personne' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 67 };
    },
    interpret: (score) => {
      if (score < 8) return { level: 'success', text: `CIWA-Ar ${score}`, detail: 'Sevrage léger - Surveillance' };
      if (score < 16) return { level: 'warning', text: `CIWA-Ar ${score}`, detail: 'Sevrage modéré - BZD à discuter' };
      if (score < 25) return { level: 'danger', text: `CIWA-Ar ${score}`, detail: 'Sevrage sévère - BZD indiquées' };
      return { level: 'danger', text: `CIWA-Ar ${score}`, detail: 'Sevrage très sévère - Réanimation, BZD IV' };
    },
    formula: 'CIWA-Ar: 10 items (0-7 ou 0-4) = 0-67'
  },

  // Cornell - Depression chez dément
  cornell: {
    name: 'Échelle de Cornell',
    category: 'Gériatrie',
    icon: Brain,
    description: 'Dépression chez le sujet dément',
    color: 'amber',
    fields: [
      { id: 'anxiety', label: 'Anxiété (expression anxieuse, rumination)', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Léger/intermittent' }, { value: 2, label: '2 - Sévère' }
      ]},
      { id: 'sadness', label: 'Tristesse', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'noReaction', label: 'Absence réaction événements agréables', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'irritability', label: 'Irritabilité', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'agitation', label: 'Agitation', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'slowness', label: 'Ralentissement (mouvements, parole, réaction)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'complaints', label: 'Plaintes physiques multiples', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'interest', label: 'Perte d\'intérêt', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'appetite', label: 'Diminution appétit', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'weightLoss', label: 'Perte de poids', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'energy', label: 'Manque d\'énergie', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'sleep', label: 'Troubles du sommeil', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'suicidal', label: 'Idéations suicidaires', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'selfEsteem', label: 'Dévalorisation de soi', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'pessimism', label: 'Pessimisme', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'delusions', label: 'Idées délirantes congruentes à l\'humeur', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 38 };
    },
    interpret: (score) => {
      if (score < 8) return { level: 'success', text: `Cornell ${score}/38`, detail: 'Pas de dépression significative' };
      if (score < 12) return { level: 'warning', text: `Cornell ${score}/38`, detail: 'Dépression légère à modérée' };
      return { level: 'danger', text: `Cornell ${score}/38`, detail: 'Dépression majeure probable' };
    },
    formula: 'Cornell: 19 items (0-2). Seuil dépression ≥ 8'
  },

  // Chumlea - Estimation taille
  chumlea: {
    name: 'Équation de Chumlea',
    category: 'Gériatrie',
    icon: Scale,
    description: 'Estimation taille selon hauteur talon-genou',
    color: 'amber',
    fields: [
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' },
        { value: 'F', label: 'Femme' }
      ]},
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 60, max: 110 },
      { id: 'kneeHeight', label: 'Hauteur talon-genou (cm)', type: 'number', min: 30, max: 70 }
    ],
    calculate: (values) => {
      const sex = values.sex || 'M';
      const age = values.age || 70;
      const kh = values.kneeHeight || 50;
      let height;
      if (sex === 'M') {
        height = 64.19 - (0.04 * age) + (2.02 * kh);
      } else {
        height = 84.88 - (0.24 * age) + (1.83 * kh);
      }
      return { score: height.toFixed(1), max: '', unit: 'cm' };
    },
    interpret: (score) => {
      return { level: 'success', text: `Taille estimée: ${score} cm`, detail: 'Formule de Chumlea pour patients ne pouvant se tenir debout' };
    },
    formula: 'Homme: 64.19 - 0.04×Age + 2.02×HG | Femme: 84.88 - 0.24×Age + 1.83×HG'
  },

  // CIRS - Comorbidités gériatrique
  cirs: {
    name: 'Score CIRS-G',
    category: 'Gériatrie',
    icon: User,
    description: 'Index de comorbidités cumulées gériatrique',
    color: 'amber',
    fields: [
      { id: 'cardiac', label: 'Cœur', type: 'select', options: [
        { value: 0, label: '0 - Pas de problème' }, { value: 1, label: '1 - Léger' },
        { value: 2, label: '2 - Modéré' }, { value: 3, label: '3 - Sévère' }, { value: 4, label: '4 - Très sévère' }
      ]},
      { id: 'vascular', label: 'HTA et vaisseaux', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'hematopoietic', label: 'Hématopoïèse', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'respiratory', label: 'Appareil respiratoire', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'eyes', label: 'Yeux, ORL', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'upper_gi', label: 'Tube digestif haut', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'lower_gi', label: 'Tube digestif bas', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'liver', label: 'Foie, pancréas, voies biliaires', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'renal', label: 'Reins', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'genitourinary', label: 'Appareil génito-urinaire', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'musculoskeletal', label: 'Appareil locomoteur', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'neurological', label: 'Système nerveux', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'endocrine', label: 'Endocrinologie', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'psychiatric', label: 'Psychiatrie', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      const count3or4 = Object.values(values).filter(v => v >= 3).length;
      return { score: total, max: 56, severe: count3or4 };
    },
    interpret: (score, calculated) => {
      const severe = calculated?.severe || 0;
      if (score < 7 && severe === 0) return { level: 'success', text: `CIRS-G ${score}/56`, detail: 'Faible comorbidité' };
      if (score < 14) return { level: 'warning', text: `CIRS-G ${score}/56`, detail: `Comorbidité modérée (${severe} organe(s) ≥3)` };
      return { level: 'danger', text: `CIRS-G ${score}/56`, detail: `Comorbidité importante (${severe} organe(s) ≥3)` };
    },
    formula: 'CIRS-G: 14 systèmes cotés 0-4. Seuil fragilité ≥ 6 ou ≥1 item à 3-4'
  }
};
